import {Box, Button, LinearProgress, Skeleton, Stack, Typography} from '@mui/material';
import {PgHistoryAgentQuota} from '_logics/LFCSourceUtil';
import {useTranslation} from 'react-i18next';
import {formatUsdCost} from './AgentCostBadge';

type Props = {
  quota: PgHistoryAgentQuota | null;
  loading: boolean;
  error: boolean;
  onRetry: () => void;
};

type DecimalParts = {
  sign: 1n | -1n;
  integer: string;
  fraction: string;
};

const parseUsd = (value: string): DecimalParts => {
  const match = value.match(/^(-?)(\d+)(?:\.(\d+))?$/);
  if (!match) return {sign: 1n, integer: '0', fraction: ''};
  return {
    sign: match[1] === '-' ? -1n : 1n,
    integer: match[2],
    fraction: match[3] || ''
  };
};

const toScaledUsd = (value: string, scale: number) => {
  const {sign, integer, fraction} = parseUsd(value);
  return sign * BigInt(`${integer}${fraction.padEnd(scale, '0')}`);
};

const formatScaledUsd = (value: bigint, scale: number) => {
  const sign = value < 0n ? '-' : '';
  const absolute = value < 0n ? -value : value;
  const digits = absolute.toString().padStart(scale + 1, '0');
  if (scale === 0) return `${sign}${digits}`;
  const integer = digits.slice(0, -scale);
  const fraction = digits.slice(-scale).replace(/0+$/, '');
  return `${sign}${integer}${fraction ? `.${fraction}` : ''}`;
};

export const formatUsdQuotaAmount = (value: string) => {
  const {fraction} = parseUsd(value);
  return formatScaledUsd(toScaledUsd(value, fraction.length), fraction.length);
};

const getQuotaProgress = (quota: PgHistoryAgentQuota) => {
  const scale = Math.max(
    quota.monthlyGrantedUsd.split('.')[1]?.length ?? 0,
    quota.monthlySpentUsd.split('.')[1]?.length ?? 0,
    quota.monthlyReservedUsd.split('.')[1]?.length ?? 0
  );
  const granted = toScaledUsd(quota.monthlyGrantedUsd, scale);
  const used =
    toScaledUsd(quota.monthlySpentUsd, scale) + toScaledUsd(quota.monthlyReservedUsd, scale);
  if (granted <= 0n || used <= 0n) return 0;
  if (used >= granted) return 100;
  return Number((used * 10_000n) / granted) / 100;
};

const AgentQuotaSummary = ({quota, loading, error, onRetry}: Props) => {
  const {t} = useTranslation();

  if (loading && quota === null) {
    return (
      <Box role="status" aria-label={t('月次クォータを読み込み中')} aria-live="polite">
        <Skeleton aria-hidden variant="rounded" width={180} height={28} />
      </Box>
    );
  }

  if (error) {
    return (
      <Stack direction="row" alignItems="center" spacing={0.5} role="alert">
        <Typography variant="caption" color="error">
          {t('月次クォータの取得に失敗しました')}
        </Typography>
        <Button size="small" onClick={onRetry}>
          {t('再試行')}
        </Button>
      </Stack>
    );
  }

  if (!quota) return null;

  if (!quota.configured) {
    return <Typography variant="caption">{t('月次クォータ未設定')}</Typography>;
  }

  const progress = getQuotaProgress(quota);

  return (
    <Box role="region" aria-label={t('月次クォータ')} sx={{minWidth: 220, ml: 'auto'}}>
      <Stack direction="row" spacing={1} justifyContent="flex-end" flexWrap="wrap" useFlexGap>
        <Typography variant="caption" sx={{textAlign: 'right'}}>
          {t('今月の利用額')} ${formatUsdCost(quota.monthlySpentUsd)}
        </Typography>
        <Typography variant="caption">
          {t('月次上限')} ${formatUsdQuotaAmount(quota.monthlyGrantedUsd)}
        </Typography>
      </Stack>
      <LinearProgress
        aria-label={t('月次クォータ使用率')}
        aria-valuetext={`${progress}%`}
        variant="determinate"
        value={progress}
        sx={{mt: 0.5, ml: 'auto'}}
      />
    </Box>
  );
};

export default AgentQuotaSummary;
